import React from "react";
import { getColor, fmt } from "./HeatmapTheme";
import { StockEntry } from "./Heatmap";
import { SectorBounds } from "./SectorOverlay";

interface NodeComponentProps {
  node: any;
  isDark: boolean;
  onHover: (stock: StockEntry, x: number, y: number) => void;
  onMove: (x: number, y: number) => void;
  onLeave: () => void;
  onSectorNode: (bounds: SectorBounds) => void;
  groupBySector: boolean;
  hideLogo: boolean;
}

const MIN_SYMBOL_W = 18;
const MIN_SYMBOL_H = 14;
const MIN_CHANGE_W = 28;
const MIN_CHANGE_H = 28;

const TreemapNode: React.FC<NodeComponentProps> = ({
  node,
  isDark,
  onHover,
  onMove,
  onLeave,
  hideLogo,
}) => {
  const { x, y, width, height, data } = node;

  const stock: StockEntry = data.stock;
  if (!stock) return null;

  const color = getColor(stock.change);
  const changeStr = `${stock.change >= 0 ? "+" : ""}${fmt(stock.change)}%`;

  const minDim = Math.min(width, height);

  const symbolSize = Math.min(Math.max(minDim * 0.22, 7), 22);
  const symbolRestSize = symbolSize * 0.6;
  const changeSize = Math.min(Math.max(minDim * 0.15, 6), 16);

  const showSymbol = width >= MIN_SYMBOL_W && height >= MIN_SYMBOL_H;
  const showChange =
    width >= MIN_CHANGE_W &&
    height >= MIN_CHANGE_H &&
    height > symbolSize + changeSize + 4;

  const symbol = stock.symbol.split(" ")[0];
  const symbolRest = stock.symbol.split(" ")[1] ?? "";

  const charFitCount = Math.floor((width - 6) / (symbolSize * 0.6));
  const displaySymbol =
    symbol.length > charFitCount ? symbol.slice(0, 5) : symbol;
  const displaySymbolRest =
    symbolRest.length > charFitCount ? symbolRest.slice(0, 5) : symbolRest;

  const showLogo =
    !hideLogo && !!stock.company_logo && width >= 20 && height >= 20;

  const logoSize = Math.min(
    Math.max(minDim * 0.25, 16),
    40,
    width - 8,
    height * 0.4
  );

  const lineGap = 2;
  const logoGap = 4;

  const totalHeight =
    (showLogo ? logoSize + logoGap : 0) +
    (showSymbol ? symbolSize : 0) +
    (symbolRest ? symbolRestSize + lineGap : 0) +
    (showChange ? changeSize + lineGap : 0);

  let currentY = y + height / 2 - totalHeight / 2;

  const logoY = showLogo ? currentY : 0;
  if (showLogo) currentY += logoSize + logoGap;

  const symbolY = currentY + symbolSize / 2;
  currentY += symbolSize;

  const symbolRestY = symbolRest ? currentY + lineGap + symbolRestSize / 2 : 0;
  if (symbolRest) currentY += lineGap + symbolRestSize;

  const changeY = showChange ? currentY + lineGap + changeSize / 2 : 0;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        stroke={isDark ? "#0f1623" : "#f1f5f9"}
        strokeWidth={0.5}
        rx={2}
        style={{ cursor: "pointer" }}
        onMouseEnter={(e) => onHover(stock, e.clientX, e.clientY)}
        onMouseMove={(e) => onMove(e.clientX, e.clientY)}
        onMouseLeave={onLeave}
      />

      {showLogo &&
        (() => {
          const cx = x + width / 2;
          const cy = logoY + logoSize / 2;
          const r = logoSize / 2;
          const clipId = `logo-clip-${stock.symbol.replace(
            /[^a-zA-Z0-9]/g,
            "_"
          )}`;

          return (
            <>
              <defs>
                <clipPath id={clipId}>
                  <circle cx={cx} cy={cy} r={r} />
                </clipPath>
              </defs>
              <circle cx={cx} cy={cy} r={r} fill="white" opacity={0.9} />
              <image
                href={`/${stock.company_logo}`}
                x={x + width / 2 - logoSize / 2}
                y={logoY}
                width={logoSize}
                height={logoSize}
                preserveAspectRatio="xMidYMid meet"
                clipPath={`url(#${clipId})`}
                style={{ pointerEvents: "none" }}
              />
            </>
          );
        })()}

      {showSymbol && (
        <text
          x={x + width / 2}
          y={symbolY}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#ffffff"
          fontSize={symbolSize}
          fontWeight={900}
          fontFamily="'Inter', 'DM Sans', sans-serif"
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {displaySymbol}
        </text>
      )}

      {symbolRest && showSymbol && (
        <text
          x={x + width / 2}
          y={symbolRestY}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255,255,255,0.75)"
          fontSize={symbolRestSize}
          fontWeight={500}
          fontFamily="'Inter', 'DM Sans', sans-serif"
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {displaySymbolRest}
        </text>
      )}

      {showChange && (
        <text
          x={x + width / 2}
          y={changeY}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255,255,255,0.88)"
          fontSize={changeSize}
          fontWeight={600}
          fontFamily="'DM Mono', 'JetBrains Mono', monospace"
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {changeStr}
        </text>
      )}
    </g>
  );
};

export default TreemapNode;
export type { NodeComponentProps };
